import {
  Alert,
  AppBar,
  Badge,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDownIcon, ChevronLeftIcon, DownloadIcon, PanelLeftOpenIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import dataUpdatedIcon from '../assets/images/data-updated-icon.svg';
import SearchPanel from '../components/Case/SearchPanel';
import FeatureFlag from '../components/FeatureFlag/FeatureFlag';
import SurfaceToggle from '../components/SimilarlySituatedBuilder/SurfaceToggle';
import { CaseWorkspaceContext } from '../hooks/useCaseWorkspace';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { useCaseApi } from '../services/api/useCase.api';
import { CaseCreate } from '../types/Case/CaseCreate.type';
import { ExportStatus } from '../types/enums/ExportStatus.enum';
import { ExportType } from '../types/enums/ExportType.enum';

/**
 * Layout used for the case workspace. Two modes: 'Discovery Snapshot' and 'Prima Facie Analysis'. Using this layout
 * keeps Search panel content on switching tabs.
 */
function CaseWorkspaceLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { caseId } = useParams<{ caseId?: string }>();
  const queryClient = useQueryClient();
  const { createCase, getCaseById, exportCase } = useCaseApi();
  const { isEnabled } = useFeatureFlags();

  const isSsbEnabled = isEnabled('SIMILARLY_SITUATED_BUILDER');
  const isSsbSurface = location.pathname.startsWith('/similarly-situated-builder');

  const [selectedDisparityId, setSelectedDisparityId] = useState<number | null>(null);
  const [selectedResourceId, setSelectedResourceId] = useState<number | null>(null);
  const [showSearchPanel, setShowSearchPanel] = useState(true);
  const [exportButtonAnchorEl, setExportButtonAnchorEl] = useState<null | HTMLElement>(null);
  const [lastExportTypeId, setLastExportTypeId] = useState<number | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const previousStatusRef = useRef<Record<string, number>>({});

  const { data: caseData, isLoading: isLoadingCase } = useQuery({
    queryKey: ['case', caseId],
    queryFn: () => getCaseById(Number(caseId)),
    enabled: !!caseId,
  });

  const { data: caseExportData } = useQuery({
    queryKey: ['caseExportData', caseId],
    queryFn: () => exportCase(Number(caseId), { dry_run: true }),
    enabled: !!caseId,
    refetchInterval: (query) => {
      const data = query.state.data;

      const hasInProgress = data?.some(
        (item) =>
          item.status?.id === ExportStatus.PENDING ||
          item.status?.id === ExportStatus.IN_PROGRESS ||
          item.status?.id === ExportStatus.FAILED,
      );
      return hasInProgress ? 2000 : false;
    },
  });

  const { mutate: createNewCase, isPending: caseCreating } = useMutation({
    mutationKey: ['createCase'],
    mutationFn: createCase,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['userCases'] });
      navigate(`/case-details/${data.id}`);
    },
  });

  const { mutate: createSsbCase, isPending: ssbCaseCreating } = useMutation({
    mutationKey: ['createSsbCase'],
    mutationFn: createCase,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['userCases'] });
      navigate(`/similarly-situated-builder/${data.id}`, { state: { startNewBuild: true } });
    },
  });

  const { mutate: caseExportDataMutation } = useMutation({
    mutationKey: ['updateCaseExportData', caseId],
    mutationFn: (exportTypeId: number) => exportCase(Number(caseId), { export_type_id: exportTypeId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['caseExportData', caseId] });
    },
    onError: (error) => {
      setErrorMessage(error?.message || 'Failed to download. Please try again.');
      setSnackbarOpen(true);
    },
  });

  const handleDisparitySelect = (disparityId: number) => {
    setSelectedDisparityId(disparityId);
    setSelectedResourceId(null);
  };

  const handleResourceSelect = (resourceId: number) => {
    setSelectedResourceId(resourceId);
    setSelectedDisparityId(null);
  };

  const handleUnselectItem = () => {
    setSelectedDisparityId(null);
    setSelectedResourceId(null);
  };

  // the similarly situated search always runs on the builder surface
  const handleSsbSearch = (newCase: CaseCreate | null) => {
    if (newCase) {
      createSsbCase(newCase);
      return;
    }

    navigate(`/similarly-situated-builder/${caseId}`, { state: { startNewBuild: true } });
  };

  const toggleExportMenu = (event?: React.MouseEvent<HTMLElement>) => {
    setExportButtonAnchorEl((prev) => (prev ? null : event?.currentTarget || null));
  };

  const handleExportAction = (exportTypeId: number) => {
    if (!caseId) return;

    const exportItem = caseExportData?.find((item) => item.type?.id === exportTypeId);

    if (!(exportItem?.download_url && exportItem.status?.id === ExportStatus.COMPLETED)) {
      caseExportDataMutation(exportTypeId);
    }

    setLastExportTypeId(exportTypeId);
    toggleExportMenu();
  };

  const disableMenuItem = (exportTypeId: number) => {
    const exportItem = caseExportData?.find((item) => item.type?.id === exportTypeId);

    return exportItem?.status?.id === ExportStatus.PENDING || exportItem?.status?.id === ExportStatus.IN_PROGRESS;
  };

  const isExportGenerating = () => {
    return caseExportData?.some(
      (item) =>
        item.status?.id === ExportStatus.IN_PROGRESS ||
        item.status?.id === ExportStatus.PENDING ||
        item.status?.id === ExportStatus.FAILED,
    );
  };

  const exportMenuItemLabel = (exportTypeId: number) => {
    const exportItem = caseExportData?.find((item) => item.type?.id === exportTypeId);
    if (!exportItem) return 'Generate';

    switch (exportItem.status?.id) {
      case ExportStatus.COMPLETED:
        return 'Download';
      case ExportStatus.IN_PROGRESS:
        return 'Processing';
      case ExportStatus.FAILED:
        return 'Retry';
      default:
        return 'Generate';
    }
  };

  const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const completedExportItem = caseExportData?.find(
    (item) => item.status?.id === ExportStatus.COMPLETED && item.type?.id === lastExportTypeId,
  );
  const [prevCompletedExportItem, setPrevCompletedExportItem] = useState(completedExportItem);
  if (completedExportItem && completedExportItem !== prevCompletedExportItem) {
    setShowCompleted(true);
    setPrevCompletedExportItem(completedExportItem);
  }
  if (!completedExportItem && prevCompletedExportItem) {
    setPrevCompletedExportItem(completedExportItem);
  }

  useEffect(() => {
    if (!showCompleted) return;
    const timer = setTimeout(() => setShowCompleted(false), 2000);
    return () => clearTimeout(timer);
  }, [showCompleted]);

  useEffect(() => {
    caseExportData?.forEach((item) => {
      const exportTypeId = item.type?.id;

      const currentStatusId = item?.status?.id;
      const previousStatusId = previousStatusRef.current[exportTypeId];

      if (
        (previousStatusId === ExportStatus.PENDING || previousStatusId === ExportStatus.IN_PROGRESS) &&
        currentStatusId === ExportStatus.COMPLETED &&
        item?.download_url
      ) {
        window.open(item.download_url, '_blank');
      }
      if (currentStatusId !== undefined) {
        previousStatusRef.current[exportTypeId] = currentStatusId;
      }
    });
  }, [caseExportData]);

  const outletContext: CaseWorkspaceContext = {
    caseData,
    isLoadingCase,
    selectedDisparityId,
    selectedResourceId,
    onDisparitySelect: handleDisparitySelect,
    onResourceSelect: handleResourceSelect,
    onUnselect: handleUnselectItem,
    isSsbEnabled,
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 84px)', paddingBottom: 2 }}>
        <AppBar
          position='sticky'
          sx={{
            top: 84,
            display: 'flex',
            justifyContent: 'center',
            background: '#fff',
            marginBottom: 2,
            height: 58,
            boxShadow: 'none',
            color: '#000',
            borderRadius: '0 !important',
            borderLeft: 0,
            borderRight: 0,
            zIndex: 998,
          }}
        >
          <Toolbar sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: '16px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', minWidth: 0 }}>
              <Tooltip title='Back to home' slotProps={{ tooltip: { sx: { borderRadius: '4px' } } }}>
                <Button
                  variant='outlined'
                  onClick={() => navigate('/')}
                  sx={{ padding: '1px', minWidth: '0px', borderRadius: '4px', borderWidth: '2px' }}
                  id='tour-back-button'
                >
                  <ChevronLeftIcon size={'24px'} color='#000' />
                </Button>
              </Tooltip>

              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '16px' }}>
                <Typography sx={{ marginLeft: '18px', fontSize: '18px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {caseData?.name || 'Untitled Search'}
                </Typography>
                {caseId &&
                  (caseData?.has_disparities_to_recalculate || (caseData?.deleted_statute_names?.length ?? 0) > 0) && (
                    <IconButton sx={{ background: '#FEEBCB !important', color: '#DD6B20 !important' }}>
                      <img src={dataUpdatedIcon} alt='Data updated icon' width={24} height={24} />
                    </IconButton>
                  )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <FeatureFlag flag='SIMILARLY_SITUATED_BUILDER'>
                <SurfaceToggle active={isSsbSurface ? 'ssb' : 'case'} caseId={caseId} />
              </FeatureFlag>
            </Box>
            {caseId && !isSsbSurface ? (
              <Box sx={{ justifySelf: 'end' }}>
                <Badge
                  badgeContent={
                    caseExportData
                      ? caseExportData.filter((item) => item.status?.id === ExportStatus.COMPLETED)?.length
                      : undefined
                  }
                  sx={{ '.MuiBadge-badge': { padding: '0px 10px', backgroundColor: '#FFAE00', color: '#fff' } }}
                >
                  <Button
                    variant='contained'
                    disableElevation
                    sx={{ width: '140px', borderRadius: '6px', paddingX: '24px' }}
                    onClick={toggleExportMenu}
                    id='tour-download-button'
                  >
                    {isExportGenerating() ? (
                      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                        <CircularProgress variant='indeterminate' color='accentYellow' size={'24px'} />
                        <Box
                          sx={{
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            position: 'absolute',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <ArrowDownIcon size={14} color='#FFAE00' />
                        </Box>
                      </Box>
                    ) : showCompleted ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>Completed</Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <DownloadIcon size={18} />
                        Download
                      </Box>
                    )}
                  </Button>
                  <Menu
                    anchorEl={exportButtonAnchorEl}
                    open={Boolean(exportButtonAnchorEl)}
                    onClose={() => toggleExportMenu()}
                    slotProps={{ paper: { sx: { minWidth: 200 } } }}
                  >
                    {[ExportType.REPORT, ExportType.DATA, ExportType.RAW_DATA].map((exportType) => {
                      const downloadUrl = caseExportData?.find((item) => item.type?.id === exportType)?.download_url;

                      return (
                        <MenuItem
                          key={exportType}
                          component='a'
                          sx={{ fontWeight: 500, fontSize: '14px' }}
                          onClick={() => handleExportAction(exportType)}
                          disabled={disableMenuItem(exportType)}
                          {...(downloadUrl ? { href: downloadUrl, download: downloadUrl } : {})}
                        >
                          {exportMenuItemLabel(exportType)}{' '}
                          {exportType === ExportType.REPORT
                            ? 'Report'
                            : exportType === ExportType.DATA
                              ? 'Data'
                              : 'Raw Data'}
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </Badge>
              </Box>
            ) : (
              <Box />
            )}
          </Toolbar>
        </AppBar>

        <Box
          sx={{ display: 'flex', flexDirection: 'row', gap: '12px', paddingX: '24px', flexGrow: 1, overflow: 'hidden' }}
        >
          {showSearchPanel ? (
            <SearchPanel
              onSubmit={createNewCase}
              isCreatingCase={caseCreating || ssbCaseCreating}
              caseData={caseData}
              isLoadingCase={isLoadingCase}
              onHidePanel={() => setShowSearchPanel(false)}
              onDisparityUnselect={handleUnselectItem}
              onSsbSearch={isSsbEnabled ? handleSsbSearch : undefined}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                backgroundColor: '#fff',
                padding: 1,
                borderRadius: 1,
                boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
                border: '1px solid #cbd5e0',
              }}
            >
              <Tooltip title='Show case details' placement='right'>
                <IconButton aria-label='Show case details' onClick={() => setShowSearchPanel(true)}>
                  <PanelLeftOpenIcon size={22} />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          <Outlet context={outletContext} />
        </Box>
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity='error' sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default CaseWorkspaceLayout;
